// Ortak varyant sıralama ve etiket yardımcıları.
//
// Kullanıcı isteği: Program genelinde varyantlar
//   1) önce renklere göre (alfabetik, TR sıralaması),
//   2) sonra bedenlere göre (mantıksal beden sırası: XXS < XS < S < M < L < XL < XXL < 3XL...
//      pantolon/ayakkabı gibi sayısal bedenler ise sayısal artan sırada)
// gruplansın. Böylece aynı renk varyantları arka arkaya, kolay seçilebilir olur.
//
// Gösterim formatı da aynı önceliği takip eder: "Renk · Beden" (örn. "Lacivert · M").

const SIZE_ORDER = [
  "XXS", "XS", "S", "M", "L", "XL", "XXL",
  "3XL", "4XL", "5XL", "6XL", "7XL", "8XL",
];

const SIZE_INDEX = new Map(SIZE_ORDER.map((s, i) => [s, i]));
// Alias'lar — kullanıcı "2XL" yazarsa da doğru yere koy
SIZE_INDEX.set("2XL", SIZE_INDEX.get("XXL"));
SIZE_INDEX.set("XXXL", SIZE_INDEX.get("3XL"));
SIZE_INDEX.set("XXXXL", SIZE_INDEX.get("4XL"));

// Attribute anahtarları — hem TR hem EN varyasyonlarını destekler.
const COLOR_KEYS = ["renk", "Renk", "color", "Color", "colour"];
const SIZE_KEYS = ["beden", "Beden", "size", "Size", "numara", "Numara"];

/** Bir attribute objesinden renk değerini bul (yoksa boş string). */
export function getColor(attrs) {
  if (!attrs) return "";
  for (const k of COLOR_KEYS) if (attrs[k]) return String(attrs[k]);
  return "";
}

/** Bir attribute objesinden beden değerini bul (yoksa boş string). */
export function getSize(attrs) {
  if (!attrs) return "";
  for (const k of SIZE_KEYS) if (attrs[k]) return String(attrs[k]);
  return "";
}

/**
 * Beden değerini sıralanabilir bir sayısal skora çevirir.
 * XS/S/M/L/XL vs. için hazır tablo; sayısal bedenler için sayı; diğerleri en sona.
 */
export function sizeSortKey(size) {
  if (!size) return [2, ""];
  const s = String(size).trim().toUpperCase();
  // Sayısal beden (pantolon/ayakkabı: 36, 38, 40 ...)
  const num = parseFloat(s.replace(",", "."));
  if (!Number.isNaN(num) && /^-?\d+([.,]\d+)?$/.test(s)) {
    return [1, num];
  }
  // Standart harfli beden
  if (SIZE_INDEX.has(s)) return [0, SIZE_INDEX.get(s)];
  // Bilinmeyen — sonlara at, ama alfabetik olarak
  return [3, s];
}

/**
 * İki bedeni karşılaştır (sıralama fonksiyonu).
 */
function compareSize(a, b) {
  const [ta, va] = sizeSortKey(a);
  const [tb, vb] = sizeSortKey(b);
  if (ta !== tb) return ta - tb;
  if (typeof va === "number" && typeof vb === "number") return va - vb;
  return String(va).localeCompare(String(vb), "tr");
}

/**
 * İki varyant objesini "önce renk, sonra beden" sırasına göre karşılaştır.
 * Kullanım: `variants.slice().sort(compareVariants)`
 */
export function compareVariants(a, b) {
  const ca = getColor(a?.attributes).toLocaleLowerCase("tr");
  const cb = getColor(b?.attributes).toLocaleLowerCase("tr");
  if (ca && cb && ca !== cb) return ca.localeCompare(cb, "tr");
  if (!ca && cb) return 1;
  if (ca && !cb) return -1;

  const sa = getSize(a?.attributes);
  const sb = getSize(b?.attributes);
  const bySize = compareSize(sa, sb);
  if (bySize !== 0) return bySize;

  // Aynı renk + beden ise SKU'ya göre sabit sıra
  return String(a?.sku || "").localeCompare(String(b?.sku || ""), "tr");
}

/** Yeni bir dizide sıralanmış kopyayı döner (orijinali değişmez). */
export function sortVariants(variants) {
  return (variants || []).slice().sort(compareVariants);
}

/**
 * "Renk · Beden" formatlı okunabilir etiket üretir.
 * - İkisi de varsa: "Lacivert · M"
 * - Sadece renk: "Lacivert"
 * - Sadece beden: "M"
 * - İkisi de yoksa: attributes'un diğer değerlerinden ilk 2'sini birleştir
 * - Hepsi boşsa: "—"
 */
export function formatVariantLabel(attributes, fallback = "—") {
  if (!attributes || typeof attributes !== "object") return fallback;
  const color = getColor(attributes);
  const size = getSize(attributes);
  const parts = [];
  if (color) parts.push(color);
  if (size) parts.push(size);
  if (parts.length > 0) return parts.join(" · ");
  // Renk/beden dışı attribute varsa hepsini birleştir
  const others = Object.entries(attributes)
    .filter(([, v]) => v)
    .map(([, v]) => String(v));
  return others.join(" · ") || fallback;
}
